// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useEffect, useState, type CSSProperties } from 'react'
import { DollarSign, Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import * as api from '../../api/client'
import SearchableSelect from '../../components/common/SearchableSelect'

const MUTE = 'var(--color-text-mute, #6b7280)'
const BORDER = 'var(--color-border, #e5e7eb)'
const usd = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export default function CostTab() {
  const [months, setMonths] = useState(6)
  const [hist, setHist] = useState<api.CostHistory | null>(null)
  const [byCust, setByCust] = useState<api.CostHistoryByCustomer | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // D-SAAS-OBS-1 Phase 2: this tab reads Azure Cost Management live (monthly history is not
  // snapshotted — only the MTD-by-resource figures the profitability waterfall needs are). Stamping
  // the fetch time makes it explicit how current these numbers are, and the banner below points at
  // Profitability, which keeps serving last known-good values when Azure throttles.
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null)

  useEffect(() => { void load() }, [months])
  async function load() {
    setLoading(true); setError(null)
    try {
      const [h, c] = await Promise.all([api.getCostHistory(months), api.getCostHistoryByCustomer(months)])
      setHist(h); setByCust(c); setFetchedAt(new Date())
    } catch (e: any) { setError(e?.message || 'Failed to load cost data') }
    finally { setLoading(false) }
  }

  const rows = hist?.months ?? []
  const max = Math.max(1, ...rows.map(r => r.total))
  const latest = rows.length ? rows[rows.length - 1] : null
  const prev = rows.length > 1 ? rows[rows.length - 2] : null
  const delta = latest && prev ? latest.total - prev.total : null

  return (
    <div style={{ maxWidth: '100%' }}>
      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, margin: '0 0 4px' }}>
            <DollarSign size={18} /> Cost management
          </h2>
          <p style={{ margin: 0, fontSize: 13, color: MUTE }}>
            Live Azure spend for the platform — monthly trend, SQL share, and per-customer breakdown. Read-only.
            {fetchedAt && <> Fetched <b>{fetchedAt.toLocaleTimeString()}</b>.</>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <SearchableSelect
            value={String(months)}
            onChange={v => setMonths(Number(v))}
            ariaLabel="Months of cost history"
            options={[{ value: '3', label: '3 months' }, { value: '6', label: '6 months' }, { value: '12', label: '12 months' }]}
            style={{ minWidth: 130 }}
          />
          <button onClick={() => void load()} disabled={loading} style={iconBtn} title="Refresh"><RefreshCw size={14} className={loading ? 'spin' : undefined} /></button>
        </div>
      </header>

      {error && <div style={errBox}><AlertCircle size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />{error}</div>}

      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: MUTE }}><Loader2 size={18} className="spin" /> Loading…</div>
      ) : hist && !hist.available ? (
        <div style={infoBox}>
          Cost data unavailable — {hist.reason || 'the managed identity may lack Cost Management Reader on the resource group.'}
          <div style={{ marginTop: 6 }}>
            Azure Cost Management rate-limits this query. Month-to-date figures on <b>Cost &amp; Profitability</b> come from
            the hourly snapshot and stay visible through a throttle; this monthly-history view reads Azure directly, so it
            recovers on its own within the hour.
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
            <div style={{ ...card, flex: '1 1 220px' }}>
              <div style={cardLabel}>Latest month{latest ? ` (${latest.month})` : ''}</div>
              <div style={{ fontSize: 32, fontWeight: 700 }}>{latest ? usd(latest.total) : '—'}</div>
              {delta != null && (
                <div style={{ fontSize: 12, color: delta > 0 ? '#b91c1c' : '#15803d', marginTop: 2 }}>
                  {delta > 0 ? '▲' : '▼'} {usd(Math.abs(delta))} vs prior month
                </div>
              )}
            </div>
            <div style={{ ...card, flex: '1 1 220px' }}>
              <div style={cardLabel}>SQL share (latest)</div>
              <div style={{ fontSize: 32, fontWeight: 700 }}>{latest ? usd(latest.sql) : '—'}</div>
              <div style={{ fontSize: 12, color: MUTE, marginTop: 2 }}>
                {latest && latest.total > 0 ? `${Math.round((latest.sql / latest.total) * 100)}% of spend` : ''}
              </div>
            </div>
          </div>

          <h3 style={h3}>Monthly spend</h3>
          <div style={box}>
            {rows.length === 0 && <p style={{ padding: 14, fontSize: 13, color: MUTE }}>No cost rows returned.</p>}
            {rows.map((r, i) => (
              <div key={r.month} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 14px', borderTop: i ? `1px solid ${BORDER}` : undefined }}>
                <span style={{ width: 64, fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{r.month}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ height: 8, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ width: `${(r.total / max) * 100}%`, height: '100%', background: '#3b3f7a', borderRadius: 99 }} />
                  </div>
                </div>
                <span style={{ width: 90, textAlign: 'right', fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{usd(r.total)}</span>
                <span style={{ width: 76, textAlign: 'right', fontSize: 12, color: MUTE, fontVariantNumeric: 'tabular-nums' }}>SQL {usd(r.sql)}</span>
              </div>
            ))}
          </div>

          <h3 style={h3}>Per customer{byCust?.available === false ? '' : ` (latest month)`}</h3>
          {byCust && !byCust.available ? (
            <div style={infoBox}>Per-customer breakdown unavailable — {byCust.reason || 'resource-level cost grouping not available.'}</div>
          ) : (
            <div style={box}>
              {(byCust?.customers ?? []).length === 0 && <p style={{ padding: 14, fontSize: 13, color: MUTE }}>No per-customer rows.</p>}
              {(byCust?.customers ?? []).map((c, i) => {
                const last = c.series.length ? c.series[c.series.length - 1].cost : 0
                return (
                  <div key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 14px', borderTop: i ? `1px solid ${BORDER}` : undefined }}>
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{c.name}</span>
                    <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{usd(last)}</span>
                  </div>
                )
              })}
            </div>
          )}
          <p style={{ fontSize: 11, color: MUTE, marginTop: 16 }}>Live from Azure Cost Management via managed identity. Values are actual pre-tax cost.</p>
        </>
      )}
    </div>
  )
}

const card: CSSProperties = { border: `1px solid ${BORDER}`, borderRadius: 8, padding: '14px 16px', background: 'var(--color-surface, #fff)' }
const cardLabel: CSSProperties = { fontSize: 12, color: MUTE, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.03em' }
const box: CSSProperties = { border: `1px solid ${BORDER}`, borderRadius: 8, overflow: 'hidden', marginBottom: 8 }
const h3: CSSProperties = { fontSize: 14, margin: '22px 0 8px', fontWeight: 600 }
const iconBtn: CSSProperties = { width: 32, height: 32, border: `1px solid ${BORDER}`, borderRadius: 6, background: 'transparent', color: 'var(--color-text, #111827)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }
const errBox: CSSProperties = { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 12px', borderRadius: 6, fontSize: 13, marginBottom: 12 }
const infoBox: CSSProperties = { background: '#f8fafc', border: `1px solid ${BORDER}`, color: MUTE, padding: '12px 14px', borderRadius: 8, fontSize: 13 }
